/**
 * Deterministic session sampling.
 *
 * A session is either IN or OUT of a sample for its whole life: the decision is
 * a pure function of the session id, so every event from one session agrees
 * (unlike per-event `Math.random()`, which shreds a session's vitals into
 * unrelated fragments) and the same session always decides the same way.
 */

/** FNV-1a 32-bit. */
function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Map a session id (plus optional salt) to a stable value in [0, 1). */
export function sessionBucket(sessionId: string, salt = ""): number {
  return fnv1a(`${salt}:${sessionId}`) / 0x1_0000_0000;
}

/**
 * Is this session inside a sample of `rate` (0..1)?
 * `rate >= 1` is always true, `rate <= 0` (or NaN) always false.
 */
export function isSessionSampled(sessionId: string, rate: number, salt = ""): boolean {
  if (!(rate > 0)) return false;
  if (rate >= 1) return true;
  return sessionBucket(sessionId, salt) < rate;
}
